/**
 * Assistant API Client
 * Handles communication with NLU backend services
 */

import {
  NLUResponse,
  VoiceBrainAssistantResponse,
  APIMode
} from '../../components/assistant/types';
import { fetchWithTimeout } from '../../utils/assistant/fetchWithTimeout';
import assistantConfig from '../../config/assistant.config';
import { logger } from '../../utils/logger';

export default class AssistantAPIClient {
  private static apiMode: APIMode = 'legacy';
  private static sessionId?: string;
  private static timeout = assistantConfig.timeout;
  private static maxRetries = 2;

  static setApiMode(mode: APIMode): void {
    this.apiMode = mode;
  }

  static setSession(sessionId: string): void {
    this.sessionId = sessionId;
  }

  static clearSession(): void {
    this.sessionId = undefined;
  }

  /**
   * Debug logging (only logs if REACT_APP_DEBUG_ASSISTANT is enabled)
   */
  private static log(message: string, data?: any): void {
    if (import.meta.env.REACT_APP_DEBUG_ASSISTANT === 'true') {
      logger.info(`[AssistantAPI] ${message}`, data || '');
    }
  }

  /**
   * Retry helper with exponential backoff
   */
  private static async retryWithBackoff<T>(
    fn: () => Promise<T>,
    retries: number = this.maxRetries,
    operationName: string = 'request'
  ): Promise<T> {
    let lastError: Error | undefined;

    for (let attempt = 0; attempt <= retries; attempt++) {
      try {
        const startTime = performance.now();
        const result = await fn();
        const duration = performance.now() - startTime;

        this.log(`${operationName} completed in ${duration.toFixed(2)}ms`, {
          attempt: attempt + 1,
          success: true
        });

        return result;
      } catch (error) {
        lastError = error as Error;

        if (attempt < retries) {
          const backoffMs = 1000 * (attempt + 1); // Linear backoff: 1s, 2s
          this.log(
            `${operationName} failed (attempt ${attempt + 1}/${
              retries + 1
            }), retrying in ${backoffMs}ms`,
            {
              error: lastError.message
            }
          );
          await new Promise((resolve) => setTimeout(resolve, backoffMs));
        }
      }
    }

    const errorMsg = `${operationName} failed after ${retries + 1} attempts: ${lastError?.message}`;
    this.log(errorMsg, { error: lastError });
    throw new Error(errorMsg);
  }

  static async sendNLUQuery(utterance: string, context: any): Promise<NLUResponse> {
    const uri = assistantConfig.nluService;

    const requestBody = {
      utterance,
      context,
      session_id: this.sessionId
    };

    this.log('Sending NLU query', { uri, utterance, context });

    // Headers for BFF proxy - authentication is via cookies (credentials: 'include')
    const headers: Record<string, string> = {
      'Content-Type': 'application/json'
    };

    return this.retryWithBackoff(
      async () => {
        const response = await fetchWithTimeout(
          uri,
          {
            method: 'POST',
            headers,
            body: JSON.stringify(requestBody),
            credentials: 'include' // Send cookies for BFF proxy auth
          },
          this.timeout
        );

        if (!response.ok) {
          const errorText = await response.text();

          // Special handling for common errors
          if (response.status === 401) {
            logger.error('[AssistantAPI] Authentication required - session may have expired');
          } else if (response.status === 403) {
            logger.error('[AssistantAPI] Access denied - no permission for this organization');
          } else if (response.status === 500 && errorText.includes('NLU service not configured')) {
            logger.error('[AssistantAPI] Backend configuration error - NLU service not configured');
          } else if (response.status === 504) {
            logger.error('[AssistantAPI] NLU service timeout');
          }

          throw new Error(`NLU request failed: ${response.status} - ${errorText}`);
        }

        const data = await response.json();
        this.log('NLU query successful', { response: data });
        return data;
      },
      this.maxRetries,
      'NLU query'
    );
  }

  static async sendEnhancedQuery(
    utterance: string,
    context: any
  ): Promise<VoiceBrainAssistantResponse> {
    if (!assistantConfig.nluEnhancedService) {
      throw new Error('Enhanced NLU service not configured');
    }

    const uri = assistantConfig.nluEnhancedService;

    const requestBody = {
      utterance,
      context,
      session_id: this.sessionId
    };

    this.log('Sending enhanced NLU query', { uri, utterance, context });

    return this.retryWithBackoff(
      async () => {
        const response = await fetchWithTimeout(
          uri,
          {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json'
            },
            body: JSON.stringify(requestBody)
          },
          this.timeout
        );

        if (!response.ok) {
          const errorText = await response.text();
          throw new Error(`Enhanced NLU request failed: ${response.status} - ${errorText}`);
        }

        const data = await response.json();
        this.log('Enhanced NLU query successful', { response: data });
        return data;
      },
      this.maxRetries,
      'Enhanced NLU query'
    );
  }

  static async sendQuery(
    utterance: string,
    context: any
  ): Promise<NLUResponse | VoiceBrainAssistantResponse> {
    if (this.apiMode === 'enhanced') {
      return this.sendEnhancedQuery(utterance, context);
    } else {
      return this.sendNLUQuery(utterance, context);
    }
  }

  static async checkReportStatus(_taskId: string): Promise<{ status: string; progress?: number }> {
    throw new Error(
      'Report status endpoint not yet configured. Check with backend team for availability.'
    );
  }

  static getConfig() {
    return {
      apiMode: this.apiMode,
      sessionId: this.sessionId,
      timeout: this.timeout,
      endpoints: {
        nlu: assistantConfig.nluService,
        enhanced: assistantConfig.nluEnhancedService
      }
    };
  }
}
